import React, { useEffect } from "react";
import {
  X,
  Check,
  User,
  CreditCard,
  Shield,
  Car,
  MapPin,
  Camera,
} from "lucide-react";
import { toast } from "sonner";

const EditVolunteerKycModal = ({
  editKycModalOpen,
  setEditKycModalOpen,
  editKycVolunteer,
  setEditKycVolunteer,
  currentStep,
  setCurrentStep,
  kycFormdata,
  setKycFormData,
  volunteers,
  handleVolunteerSelect,
  handleFileChange,
  handleStepSubmit,
  uploadImageToServer,
  resetForm,
  Loading,
  onSuccess,
  activeTab,
  fetchVolunteerKycDetails,
  fetchAllVolunteers,
  AdminId,
  volunteer,
}) => {
  const steps = [
    { id: 1, label: "Address", icon: MapPin },
    { id: 2, label: "Profile Picture", icon: Camera },
    { id: 3, label: "Aadhaar Card", icon: CreditCard },
    { id: 4, label: "PAN Card", icon: Shield },
  ];

  const handleClose = () => {
    setEditKycModalOpen(false);
    setEditKycVolunteer(null);
    setCurrentStep(1);
    resetForm();
  };

  const handleNextStep = async (stepNumber, stepData) => {
    if (!kycFormdata.volunteer_code && stepNumber === 1) {
      toast.error("Please select a volunteer");
      return;
    }

    const success = await handleStepSubmit(stepNumber, stepData);

    if (success) {
      if (stepNumber < steps.length) {
        setCurrentStep(stepNumber + 1);
      } else {
        toast.success("All KYC steps completed successfully!");

        setTimeout(() => {
          handleClose();
          if (activeTab === "Kyc Details") {
            fetchVolunteerKycDetails(kycFormdata.volunteer_code);
          }
          if (onSuccess) onSuccess();
        }, 1500);
      }
    }
  };

  useEffect(() => {
    if (AdminId) {
      fetchAllVolunteers(AdminId);
    }
  }, [AdminId]);

  useEffect(() => {
    console.log("Volunteer list:", volunteer);
  }, [volunteer]);

  if (!editKycModalOpen) return null;

  return (
    <div>
      {editKycModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 relative mx-4">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-4 border-b">
              <h2 className="text-xl font-semibold">Edit KYC Data</h2>
              <button
                onClick={() => {
                  setEditKycModalOpen(false);
                  setEditKycVolunteer(null);
                  setCurrentStep(1);
                  resetForm();
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-100 to-amber-100 p-8 rounded-2xl relative overflow-hidden">
              {/* Floating bubbles background */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full bg-white bg-opacity-30"
                    style={{
                      width: `${Math.random() * 25 + 15}px`,
                      height: `${Math.random() * 25 + 15}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 3}s`,
                      animationDuration: `${4 + Math.random() * 3}s`,
                    }}
                  />
                ))}
              </div>

              {/* Additional floating elements */}
              <div className="absolute inset-0">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={`extra-${i}`}
                    className="absolute rounded-full bg-blue-200 bg-opacity-20"
                    style={{
                      width: `${Math.random() * 30 + 10}px`,
                      height: `${Math.random() * 30 + 10}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`,
                      animationDuration: `${3 + Math.random() * 2}s`,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10">
                <div className="flex items-center justify-center space-x-8">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;

                    return (
                      <div key={step.id} className="flex flex-col items-center">
                        <div
                          className={`relative w-16 h-16 rounded-full transition-all duration-500 flex items-center justify-center ${
                            isCompleted
                              ? "bg-green-400 text-white shadow-lg transform scale-110"
                              : isCurrent
                                ? "bg-blue-400 text-white shadow-xl transform scale-125"
                                : "bg-white text-gray-400 shadow-md"
                          }`}
                        >
                          {/* Ripple effect for current step */}
                          {isCurrent && (
                            <>
                              <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-30"></div>
                              <div className="absolute inset-0 rounded-full bg-blue-300 animate-ping opacity-20"></div>
                            </>
                          )}

                          {/* Glow effect for completed steps */}
                          {isCompleted && (
                            <div className="absolute inset-0 rounded-full bg-green-300 animate-pulse opacity-40"></div>
                          )}

                          <div className="relative z-10">
                            {isCompleted ? (
                              <Check size={24} />
                            ) : (
                              <StepIcon size={24} />
                            )}
                          </div>
                        </div>

                        <span
                          className={`mt-4 text-sm font-medium text-center ${
                            isCompleted
                              ? "text-green-600"
                              : isCurrent
                                ? "text-blue-600"
                                : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              {currentStep === 1 && (
                <div className="flex justify-center">
                  <div className="w-full max-w-5xl bg-white rounded-2xl shadow-sm mt-5 p-8 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Volunteer Address Information
                    </h3>
                    {!editKycVolunteer && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Volunteer{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={kycFormdata.volunteer_code}
                          onChange={handleVolunteerSelect}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                shadow-sm mb-4"
                          required
                        >
                          <option value="">Choose Volunteer</option>
                          {volunteers?.map((volunteer) => (
                            <option
                              key={volunteer?.volunteer_code}
                              value={volunteer?.volunteer_code}
                            >
                              {volunteer.first_name ||
                                volunteer.firstName ||
                                ""}{" "}
                              {volunteer.last_name || volunteer.lastName || ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.city}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                shadow-sm"
                          placeholder="Enter City"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Area <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.area}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              area: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                  shadow-sm"
                          placeholder="Enter Area"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex justify-end mt-10">
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.city ||
                            !kycFormdata.area ||
                            !kycFormdata.volunteer_code
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          const step1Data = {
                            volunteer_code: kycFormdata.volunteer_code,
                            step1: {
                              city: kycFormdata.city,
                              area: kycFormdata.area,
                            },
                          };

                          const success = await handleStepSubmit(1, step1Data);
                          if (success) {
                            setCurrentStep(2);
                            console.log("STEP NUMBER:", step1Data);
                            console.log("FORM DATA RECEIVED:", step1Data);
                          }
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
                                  rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex justify-center">
                  <div className="w-full max-w-3xl bg-white rounded-2xl shadow-sm p-8 mt-5 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Upload Profile Picture
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 w-full">
                        <label
                          htmlFor="profile_photo"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Profile Photo <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.profile_photo ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.profile_photo === "string"
                                  ? `${
                                      kycFormdata.profile_photo
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.profile_photo,
                                    )
                              }
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded-full mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.profile_photo === "string"
                                ? "Current Profile Photo"
                                : kycFormdata.profile_photo.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "profile_photo",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_profile_photo"
                              />
                              <label
                                htmlFor="change_profile_photo"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
      rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("profile_photo", null)
                                }
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
      rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "profile_photo",
                                  e.target.files[0],
                                )
                              }
                              className="hidden"
                              id="profile_photo"
                            />
                            <label
                              htmlFor="profile_photo"
                              className="cursor-pointer bg-white text-blue-600 border 
                                    border-blue-600 px-4 py-2 rounded-lg 
                                    hover:bg-blue-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
      rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!kycFormdata.profile_photo) {
                            toast.error("Please upload profile photo");
                            return;
                          }

                          try {
                            let imageUrl = kycFormdata.profile_photo;

                            if (kycFormdata.profile_photo instanceof File) {
                              imageUrl = await uploadImageToServer(
                                kycFormdata.profile_photo,
                                kycFormdata.volunteer_code,
                                "profile_photo",
                              );
                              setKycFormData((prev) => ({
                                ...prev,
                                profile_photo: imageUrl,
                              }));
                            }

                            const step2Data = {
                              volunteer_code: kycFormdata.volunteer_code,
                              step2: {
                                profilePicture: imageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              2,
                              step2Data,
                            );
                            if (success) {
                              setCurrentStep(3);
                            }
                          } catch (error) {
                            console.error("Profile upload error:", error);
                            toast.error("Failed to upload profile picture");
                          }
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
                              rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="flex justify-center mt-5">
                  <div className="w-full max-w-6xl bg-white rounded-2xl shadow-sm p-10 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Aadhaar Information
                    </h3>

                    <div className="mb-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aadhaar Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={kycFormdata.aadhaar_no}
                        onChange={(e) =>
                          setKycFormData((prev) => ({
                            ...prev,
                            aadhaar_no: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
            shadow-sm"
                        placeholder="Enter Aadhaar number"
                        maxLength="12"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                        <label
                          htmlFor="aadhaar_front"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Aadhaar Front <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.aadhaar_front ? (
                          <div className="space-y-3 text-center w-full">
                            <img
                              src={
                                typeof kycFormdata.aadhaar_front === "string"
                                  ? `${
                                      kycFormdata.aadhaar_front
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.aadhaar_front,
                                    )
                              }
                              alt="Aadhaar Front"
                              className="w-full max-h-64 object-contain rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.aadhaar_front === "string"
                                ? "Current Aadhaar Front"
                                : kycFormdata.aadhaar_front.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "aadhaar_front",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_aadhaar_front"
                              />
                              <label
                                htmlFor="change_aadhaar_front"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("aadhaar_front", null)
                                }
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "aadhaar_front",
                                  e.target.files[0],
                                )
                              }
                              className="hidden"
                              id="aadhaar_front"
                            />
                            <label
                              htmlFor="aadhaar_front"
                              className="cursor-pointer bg-white text-blue-600 border 
                  border-blue-600 px-4 py-2 rounded-lg 
                  hover:bg-blue-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                        <label
                          htmlFor="aadhaar_back"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Aadhaar Back <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.aadhaar_back ? (
                          <div className="space-y-3 text-center w-full">
                            <img
                              src={
                                typeof kycFormdata.aadhaar_back === "string"
                                  ? `${
                                      kycFormdata.aadhaar_back
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.aadhaar_back,
                                    )
                              }
                              alt="Aadhaar Back"
                              className="w-full max-h-64 object-contain rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.aadhaar_back === "string"
                                ? "Current Aadhaar Back"
                                : kycFormdata.aadhaar_back.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "aadhaar_back",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_aadhaar_back"
                              />
                              <label
                                htmlFor="change_aadhaar_back"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("aadhaar_back", null)
                                }
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "aadhaar_back",
                                  e.target.files[0],
                                )
                              }
                              className="hidden"
                              id="aadhaar_back"
                            />
                            <label
                              htmlFor="aadhaar_back"
                              className="cursor-pointer bg-white text-blue-600 border 
                  border-blue-600 px-4 py-2 rounded-lg 
                  hover:bg-blue-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
            rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.aadhaar_no ||
                            !kycFormdata.aadhaar_front ||
                            !kycFormdata.aadhaar_back
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          try {
                            let frontImageUrl = kycFormdata.aadhaar_front;
                            let backImageUrl = kycFormdata.aadhaar_back;

                            if (typeof kycFormdata.aadhaar_front !== "string") {
                              frontImageUrl = await uploadImageToServer(
                                kycFormdata.aadhaar_front,
                                kycFormdata.volunteer_code,
                                "aadhaar_front",
                              );
                            }

                            if (typeof kycFormdata.aadhaar_back !== "string") {
                              backImageUrl = await uploadImageToServer(
                                kycFormdata.aadhaar_back,
                                kycFormdata.volunteer_code,
                                "aadhaar_back",
                              );
                            }

                            const step3Data = {
                              volunteer_code: kycFormdata.volunteer_code,
                              step3: {
                                aadharNumber: kycFormdata.aadhaar_no,
                                aadharFrontPhoto: frontImageUrl,
                                aadharBackPhoto: backImageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              3,
                              step3Data,
                            );
                            if (success) {
                              setCurrentStep(4);
                            }
                          } catch (error) {
                            toast.error("Failed to upload Aadhaar images");
                            console.error("Aadhaar upload error:", error);
                          }
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
            rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="flex justify-center mt-5">
                  <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm p-8 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      PAN Card Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PAN Card Number{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.pan_no}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              pan_no: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                        shadow-sm"
                          placeholder="Enter PAN card number"
                          maxLength="10"
                          required
                        />
                      </div>

                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                        <label
                          htmlFor="pan_front"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          PAN Card Front Image{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.pan_front ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.pan_front === "string"
                                  ? kycFormdata.pan_front
                                  : URL.createObjectURL(kycFormdata.pan_front)
                              }
                              alt="PAN Front"
                              className="w-32 h-25 object-cover rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.pan_front === "string"
                                ? "Current PAN Card"
                                : kycFormdata.pan_front.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "pan_front",
                                    e.target.files[0],
                                  )
                                }
                                className="hidden"
                                id="change_pan_front"
                              />
                              <label
                                htmlFor="change_pan_front"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                            rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("pan_front", null)
                                }
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                            rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange("pan_front", e.target.files[0])
                              }
                              className="hidden"
                              id="pan_front"
                            />
                            <label
                              htmlFor="pan_front"
                              className="cursor-pointer bg-white text-blue-600 border 
                          border-blue-600 px-4 py-2 rounded-lg 
                          hover:bg-blue-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
    rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          if (!kycFormdata.pan_no || !kycFormdata.pan_front) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          try {
                            let panImageUrl = kycFormdata.pan_front;

                            if (typeof kycFormdata.pan_front !== "string") {
                              panImageUrl = await uploadImageToServer(
                                kycFormdata.pan_front,
                                kycFormdata.volunteer_code,
                                "pan_front",
                              );
                            }
                            const step4Data = {
                              volunteer_code: kycFormdata.volunteer_code,
                              step4: {
                                panCard: kycFormdata.pan_no,
                                panCardFrontPhoto: panImageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              4,
                              step4Data,
                            );

                            if (success) {
                              toast.success(
                                "All KYC steps completed successfully!",
                              );

                              setTimeout(() => {
                                handleClose();

                                if (activeTab === "Kyc Details") {
                                  fetchVolunteerKycDetails(
                                    kycFormdata.volunteer_code,
                                  );
                                }

                                if (onSuccess) onSuccess();
                              }, 1200);
                            }
                          } catch (error) {
                            toast.error("Failed to upload PAN card image");
                            console.error("PAN upload error:", error);
                          }
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 
    rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditVolunteerKycModal;
